import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Center,
  Field,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Spinner,
  Tabs,
  Text,
  Textarea,
  VStack,
  Badge,
} from '@chakra-ui/react';
import { HiPlus, HiTrash, HiArrowPath, HiSparkles } from 'react-icons/hi2';
import { useActiveOrganization } from '@aegix/auth-organization';
import { useUser } from '@aegix/auth-identity';
import type { SMSUser, ActiveOrganization } from '../../lib/sms-auth';
import {
  useInvitationEmailTemplates,
  useCreateInvitationEmailTemplate,
  useUpdateInvitationEmailTemplate,
  useDeleteInvitationEmailTemplate,
} from '../../hooks/queries/useInvitationEmailTemplates';
import { toaster } from '../ui/toaster';

const DEFAULT_TEMPLATES = {
  role: `<p>Hello {{ invitee_name }},</p>
<p>You have been invited by {{ inviter_name }} to sign up for {{ org_name }} ({{ site_name }}'s Alert Monitor). Please create your account by following the link below:</p>
<p style="margin: 24px 0;">
  <a href="{{ invite_url }}" style="background-color: #1a252d; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 500; display: inline-block;">Sign Up and Register</a>
</p>
<p>After registering, you will be able to sign in via the web or download our mobile app.</p>
<p>If you have any issues logging in, please contact support.</p>
<p>Thanks,<br>{{ inviter_name }}</p>`,

  shared: `<p>Hello,</p>
<p>You have been invited by {{ inviter_name }} to sign up for the shared account for {{ org_name }} ({{ site_name }}'s Alert Monitor). Please create your account by following the link below:</p>
<p style="margin: 24px 0;">
  <a href="{{ invite_url }}" style="background-color: #1a252d; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 500; display: inline-block;">Sign Up and Register</a>
</p>
<p>After registering, you will be able to sign in via the web or download our mobile app.</p>
<p>If you have any issues logging in, please contact support.</p>
<p>Thanks,<br>{{ inviter_name }}</p>`,
};

const VARIABLE_METADATA: Record<string, { label: string; desc: string }> = {
  invitee_name: { label: 'Invitee Name', desc: "Invited user's full name" },
  org_name: { label: 'Organization Name', desc: 'Organization name' },
  site_name: { label: 'Site Name', desc: 'Site name' },
  invite_url: { label: 'Invite URL', desc: 'Full invite URL' },
  inviter_name: { label: 'Inviter Name', desc: "Inviter's full name" },
};

export function InvitationTemplatesContent() {
  const { t } = useTranslation();
  const user = useUser<SMSUser>();
  const activeOrganization = useActiveOrganization() as ActiveOrganization | null;

  const { data: templates, isLoading, error } = useInvitationEmailTemplates();
  const createMutation = useCreateInvitationEmailTemplate();
  const updateMutation = useUpdateInvitationEmailTemplate();
  const deleteMutation = useDeleteInvitationEmailTemplate();

  const [selectedVariant, setSelectedVariant] = useState<'role' | 'shared'>('role');
  const [bodyHtml, setBodyHtml] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const activeTemplate = templates?.find((t) => t.variant === selectedVariant);

  const availableVars = activeTemplate?.available_variables || [
    'invitee_name',
    'org_name',
    'site_name',
    'invite_url',
    'inviter_name',
  ];

  const variablesToRender = availableVars.map((name) => ({
    name,
    label: VARIABLE_METADATA[name]?.label || name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    desc: VARIABLE_METADATA[name]?.desc || `Template variable: ${name}`,
  }));

  // Synchronize local state when selected tab or query finishes
  useEffect(() => {
    if (activeTemplate) {
      setBodyHtml(activeTemplate.body_html);
    } else {
      setBodyHtml(DEFAULT_TEMPLATES[selectedVariant]);
    }
  }, [activeTemplate, selectedVariant]);

  if (isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={12}>
        <Text color="text.danger">
          {error instanceof Error ? error.message : t('invitationTemplates.failedLoad', 'Failed to load templates')}
        </Text>
      </Center>
    );
  }

  const handleInsertVariable = (variableName: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    const token = `{{ ${variableName} }}`;
    
    setBodyHtml(before + token + after);

    // Reposition cursor after inserting
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + token.length;
    }, 0);
  };

  const handleSave = async () => {
    if (!activeOrganization?.id) {
      toaster.create({ title: t('invitationTemplates.noOrgSelected', 'No active organization selected'), type: 'error' });
      return;
    }

    try {
      if (activeTemplate) {
        await updateMutation.mutateAsync({
          publicId: activeTemplate.public_id,
          data: { body_html: bodyHtml },
        });
        toaster.create({ title: t('invitationTemplates.updatedSuccess', 'Template updated successfully'), type: 'success' });
      } else {
        await createMutation.mutateAsync({
          district: activeOrganization.id,
          variant: selectedVariant,
          body_html: bodyHtml,
        });
        toaster.create({ title: t('invitationTemplates.savedSuccess', 'Custom template saved successfully'), type: 'success' });
      }
    } catch (err) {
      toaster.create({
        title: t('invitationTemplates.failedSave', 'Failed to save template'),
        description: err instanceof Error ? err.message : t('common.unexpectedError', 'An unexpected error occurred.'),
        type: 'error',
      });
    }
  };

  const handleDelete = async () => {
    if (!activeTemplate) return;
    if (
      !window.confirm(
        t('invitationTemplates.confirmDelete', 'Are you sure you want to delete this custom template? The system will revert to the default template.')
      )
    ) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(activeTemplate.public_id);
      toaster.create({ title: t('invitationTemplates.revertedSuccess', 'Template reverted to default'), type: 'success' });
    } catch (err) {
      toaster.create({
        title: t('invitationTemplates.failedRevert', 'Failed to revert template'),
        description: err instanceof Error ? err.message : t('common.unexpectedError', 'An unexpected error occurred.'),
        type: 'error',
      });
    }
  };

  // Interpolate variables for preview
  const previewHtml = () => {
    let rendered = bodyHtml || '';
    const orgName = activeOrganization?.name || 'Oakridge School District';
    const inviterName =
      user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Jane Administrator' : 'Jane Administrator';

    const replacements: Record<string, string> = {
      '{{ invitee_name }}': 'John Doe',
      '{{ org_name }}': orgName,
      '{{ site_name }}': 'Oakridge High School',
      '{{ invite_url }}': 'https://alert.aegix.app/invite/register/mock-token',
      '{{ inviter_name }}': inviterName,
    };

    Object.entries(replacements).forEach(([variable, value]) => {
      const escapedVar = variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      rendered = rendered.replace(new RegExp(escapedVar, 'g'), value);
    });

    return rendered;
  };

  const isCustom = !!activeTemplate;
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Box px={{ base: 4, md: 8 }} py={6}>
      <Flex direction="column" gap={4} mb={6}>
        <Flex justify="space-between" align="start">
          <Box>
            <Heading size="md" color="text.primary" mb={1}>
              {t('invitationTemplates.title', 'Invitation Email Templates')}
            </Heading>
            <Text fontSize="sm" color="text.secondary">
              {t('invitationTemplates.subtitle', 'Customize the emails sent when inviting new users to your organization ({{orgName}}).', { orgName: activeOrganization?.name || '' })}
            </Text>
          </Box>
          <HStack gap={2}>
            {isCustom ? (
              <Badge colorScheme="green" variant="solid" px={3} py={1} borderRadius="full">
                {t('invitationTemplates.customActive', 'Custom Template Active')}
              </Badge>
            ) : (
              <Badge colorScheme="orange" variant="outline" px={3} py={1} borderRadius="full">
                {t('invitationTemplates.usingDefault', 'Using System Default')}
              </Badge>
            )}
          </HStack>
        </Flex>
      </Flex>

      <Tabs.Root
        value={selectedVariant}
        onValueChange={(details) => setSelectedVariant(details.value as 'role' | 'shared')}
        variant="enclosed"
        mb={5}
      >
        <Tabs.List>
          <Tabs.Trigger value="role" data-e2e="tab-variant-role">
            {t('invitationTemplates.roleTab', 'Individual Invite (Named User)')}
          </Tabs.Trigger>
          <Tabs.Trigger value="shared" data-e2e="tab-variant-shared">
            {t('invitationTemplates.sharedTab', 'Shared Login Invite (Role User)')}
          </Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>

      <Grid templateColumns={{ base: '1fr', xl: '7fr 5fr' }} gap={6}>
        {/* Editor Side */}
        <GridItem>
          <VStack align="stretch" gap={4} p={5} bg="surface.primary" border="1px solid" borderColor="border.primary" borderRadius="lg">
            <Box>
              <Text fontWeight="600" fontSize="sm" mb={2} color="text.primary">
                {t('invitationTemplates.insertVariables', 'Insert Variables')}
              </Text>
              <Flex gap={2} flexWrap="wrap">
                {variablesToRender.map((v) => (
                  <Button
                    key={v.name}
                    size="xs"
                    variant="outline"
                    onClick={() => handleInsertVariable(v.name)}
                    title={v.desc}
                    borderRadius="md"
                    borderColor="border.subtle"
                    _hover={{ bg: 'surface.secondary' }}
                  >
                    <HiPlus style={{ marginRight: '4px' }} /> {v.label}
                  </Button>
                ))}
              </Flex>
            </Box>

            <Field.Root gap={0}>
              <Field.Label fontWeight="600" fontSize="sm" mb={2} color="text.primary">
                {t('invitationTemplates.htmlBodyEditor', 'HTML Body Editor')}
              </Field.Label>
              <Textarea
                ref={textareaRef}
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                rows={14}
                fontFamily="mono"
                fontSize="xs"
                placeholder={t('invitationTemplates.htmlBodyPlaceholder', 'Write HTML body here...')}
                borderColor="border.primary"
                borderRadius="md"
                bg="surface.secondary"
                _focus={{ borderColor: 'button.primary' }}
              />
            </Field.Root>

            <Flex justify="space-between" mt={2}>
              <HStack gap={3}>
                <Button
                  colorScheme="teal"
                  bg="button.primary"
                  color="white"
                  _hover={{ bg: 'button.primaryHover' }}
                  onClick={handleSave}
                  loading={isPending}
                  disabled={isPending}
                  data-e2e="save-template-button"
                >
                  {t('invitationTemplates.saveTemplate', 'Save Template')}
                </Button>
                {isCustom && (
                  <Button
                    variant="ghost"
                    color="text.danger"
                    _hover={{ bg: 'bg.error' }}
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    data-e2e="reset-template-button"
                  >
                    <HiTrash style={{ marginRight: '6px' }} /> {t('invitationTemplates.resetToDefault', 'Reset to Default')}
                  </Button>
                )}
              </HStack>
              {!isCustom && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setBodyHtml(DEFAULT_TEMPLATES[selectedVariant])}
                  title={t('invitationTemplates.resetDraftTitle', 'Revert draft back to default system template text')}
                >
                  <HiArrowPath style={{ marginRight: '6px' }} /> {t('invitationTemplates.resetDraft', 'Reset Draft')}
                </Button>
              )}
            </Flex>
          </VStack>
        </GridItem>

        {/* Live Preview Side */}
        <GridItem>
          <VStack align="stretch" gap={4}>
            <Text fontWeight="600" fontSize="sm" color="text.primary">
              {t('invitationTemplates.livePreview', 'Live Preview')}
            </Text>
            <Box
              border="1px solid"
              borderColor="border.primary"
              borderRadius="lg"
              overflow="hidden"
              bg="surface.primary"
              shadow="sm"
            >
              {/* Mock Email Client Header */}
              <Box bg="surface.secondary" p={4} borderBottom="1px solid" borderColor="border.primary">
                <Grid templateColumns="70px 1fr" gap={2} fontSize="xs" color="text.secondary">
                  <Text fontWeight="500">{t('common.from', 'From:')}</Text>
                  <Text color="text.primary">Aegix Alerts &lt;no-reply@aegix.app&gt;</Text>

                  <Text fontWeight="500">{t('common.to', 'To:')}</Text>
                  <Text color="text.primary">john.doe@example.com</Text>

                  <Text fontWeight="500">{t('common.subject', 'Subject:')}</Text>
                  <Text color="text.primary" fontWeight="600">
                    {t('invitationTemplates.mockSubject', 'You have been invited to join {{orgName}}', { orgName: activeOrganization?.name || 'Oakridge School District' })}
                  </Text>
                </Grid>
              </Box>

              {/* Email Content Frame */}
              <Box p={2} bg="#ffffff">
                <iframe
                  title={t('invitationTemplates.previewTitle', 'Invitation Email Preview')}
                  sandbox="allow-same-origin"
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <meta charset="utf-8">
                        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet">
                        <style>
                          body {
                            font-family: 'Inter', 'Helvetica Neue', Helvetica, serif;
                            margin: 0;
                            padding: 16px;
                            color: #2d3748;
                            background-color: #ffffff;
                            font-size: 14px;
                            line-height: 1.6;
                            -webkit-font-smoothing: antialiased;
                          }
                          table {
                            max-width: 100%;
                          }
                        </style>
                      </head>
                      <body>
                        ${previewHtml()}
                      </body>
                    </html>
                  `}
                  style={{
                    width: '100%',
                    height: '420px',
                    border: 'none',
                    backgroundColor: '#ffffff',
                  }}
                />
              </Box>
            </Box>
            <HStack fontSize="xs" color="text.secondary" p={2} bg="surface.secondary" borderRadius="md" gap={2}>
              <HiSparkles color="#d69e2e" />
              <Text>{t('invitationTemplates.simulatedNote', 'Variables are simulated with mock values for preview purposes.')}</Text>
            </HStack>
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
}
